import { useState } from "react";
import { useLocation } from "wouter";
import { Map } from "lucide-react";
import { SERVICE_ACTIVITY_CATEGORY_LABEL } from "@shared/serviceActivityCategory";
import { SERVICE_OPS_LIMITS, type ServiceOperationsView } from "@shared/serviceOperations";
import { formatSingaporeDateTime } from "@shared/timezone";
import { useLiveElapsed } from "@/components/roWorkTimer/useLiveElapsed";
import { ACTIVITY_BADGE_CLASS, ACTIVITY_DOT_CLASS } from "@/components/service/activityCategoryStyles";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { DashboardPanel } from "./DashboardPanel";
import { DashboardSectionHeader } from "./DashboardSectionHeader";
import { TodaysFieldSchedules } from "./TodaysFieldSchedules";

function focusSection(id: string) {
  document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
}

function SnapshotCard({
  label,
  value,
  target,
}: {
  label: string;
  value: number;
  target: string;
}) {
  return (
    <button
      type="button"
      onClick={() => focusSection(target)}
      className="min-w-[120px] rounded-xl border border-slate-200 bg-slate-50/70 px-3 py-2 text-left hover:border-slate-300 hover:bg-white"
    >
      <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">{label}</p>
      <p className="mt-0.5 text-xl font-semibold tracking-tight text-slate-900">{value}</p>
    </button>
  );
}

function ActiveRepairCard({
  repair,
  onOpen,
}: {
  repair: ServiceOperationsView["happeningNow"]["repairs"][number];
  onOpen: () => void;
}) {
  const elapsed = useLiveElapsed(repair.startedAt);
  const started = formatSingaporeDateTime(repair.startedAt, { hour: "2-digit", minute: "2-digit", hour12: false });
  return (
    <button type="button" onClick={onOpen} data-testid="service-active-repair" className="w-full rounded-xl border border-white/15 border-l-4 border-l-violet-400 bg-white p-4 text-left shadow-sm hover:bg-slate-50">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-mono text-[11px] text-slate-500">{repair.jobNumber}</p>
          <p className="mt-1 truncate text-sm font-semibold text-slate-900">{repair.customerName}</p>
          <p className="truncate text-sm text-slate-600">{repair.equipment}</p>
        </div>
        <span className="inline-flex items-center gap-1.5 rounded-full bg-violet-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-violet-800">
          <span className="h-1.5 w-1.5 rounded-full bg-violet-500 motion-safe:animate-pulse" aria-hidden />
          {repair.stageLabel}
        </span>
      </div>
      <p className="mt-3 font-mono text-lg font-semibold tracking-tight text-slate-900">{elapsed}</p>
      <p className="mt-1 text-xs text-slate-600">{repair.technicianName} · started {started}</p>
      <p className="mt-1 text-xs text-slate-500">{repair.statusLabel}</p>
    </button>
  );
}

function RowButton({
  onClick,
  children,
  testId,
}: {
  onClick: () => void;
  children: React.ReactNode;
  testId?: string;
}) {
  return (
    <button type="button" data-testid={testId} onClick={onClick} className="w-full rounded-xl px-3 py-3 text-left hover:bg-slate-50">
      {children}
    </button>
  );
}

export function ServiceOperationsDashboard({ data }: { data: ServiceOperationsView }) {
  const [, setLocation] = useLocation();
  const [reportsOpen, setReportsOpen] = useState(false);
  const preview = SERVICE_OPS_LIMITS.reportsPreview;
  const reports = reportsOpen ? data.latestReports.items : data.latestReports.items.slice(0, preview);
  const hiddenReports = Math.max(0, data.latestReports.totalBounded - preview);
  const openRo = (id: number) => setLocation(`/repair-orders/id/${id}`);
  const openVisit = (id: number) => setLocation(`/site-visits/id/${id}`);

  return (
    <div data-testid="service-command-centre" className="space-y-6">
      <section data-testid="service-daily-calendar">
        <DashboardPanel className="overflow-hidden">
          <div className="border-b border-slate-100 px-5 py-4">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <DashboardSectionHeader
                title="Service calendar"
                subtitle="Technician schedules, site visits and availability for the selected day"
                prominent
              />
              <div className="flex flex-wrap items-center justify-end gap-3">
                <section data-testid="service-snapshot" className="grid grid-cols-2 gap-2 xl:grid-cols-4">
                  <SnapshotCard label="Working now" value={data.snapshot.workingNow} target="service-happening-now" />
                  <SnapshotCard label="Visits today" value={data.snapshot.visitsToday} target="service-upcoming-visits" />
                  <SnapshotCard label="Needs attention" value={data.snapshot.needsAttention} target="service-outstanding-ros" />
                  <SnapshotCard label="Completed recently" value={data.snapshot.completedRecently} target="service-recently-fixed" />
                </section>
                <Button
                  variant="outline"
                  size="sm"
                  className="shrink-0 text-xs"
                  onClick={() => setLocation("/calendar/field")}
                >
                  <Map className="mr-1 h-3.5 w-3.5" />
                  Field Calendar
                </Button>
              </div>
            </div>
          </div>
          <div className="p-4 sm:p-5">
            <TodaysFieldSchedules />
          </div>
        </DashboardPanel>
      </section>

      <section id="service-happening-now" data-testid="service-happening-now" className="rounded-2xl bg-[#0f1c3f] p-5 text-white shadow-sm sm:p-6">
        <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-300">Happening now</p>
        <h2 className="mt-1 text-lg font-semibold">Active repair orders and site visits</h2>
        {data.happeningNow.repairs.length === 0 && data.happeningNow.visits.length === 0 ? (
          <p data-testid="service-happening-empty" className="mt-4 text-sm text-slate-300">No repair orders or site visits are active right now.</p>
        ) : (
          <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
            <div className="rounded-xl border border-white/10 bg-white/[0.04] p-3">
              <div className="mb-3 flex items-center justify-between">
                <h3 className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-200">Site visits in progress</h3>
                <span className="rounded-full bg-emerald-400 px-2 py-0.5 text-xs font-bold text-[#0f1c3f]">{data.happeningNow.visits.length}</span>
              </div>
              <div className="space-y-3">
                {data.happeningNow.visits.length === 0 ? <p className="py-4 text-center text-sm text-slate-400">No active site visits.</p> : data.happeningNow.visits.map((visit) => (
                  <button key={visit.id} type="button" data-testid="service-active-visit" onClick={() => openVisit(visit.id)} className="w-full rounded-xl border border-white/15 border-l-4 border-l-emerald-400 bg-white p-4 text-left text-slate-900 shadow-sm hover:bg-slate-50">
                    <p className="font-mono text-[11px] text-slate-500">{visit.visitNumber}</p>
                    <p className="mt-1 text-sm font-semibold">{visit.customerName}</p>
                    {visit.siteAddress ? <p className="truncate text-sm text-slate-600">{visit.siteAddress}</p> : null}
                    <p className="mt-3 text-xs font-semibold uppercase tracking-wide text-emerald-700">Visit in progress</p>
                    <p className="mt-1 text-xs text-slate-600">{visit.technicianName || "Technician"}{visit.startedAt ? ` · arrived ${formatSingaporeDateTime(visit.startedAt, { hour: "2-digit", minute: "2-digit", hour12: false })}` : ""}</p>
                    <p className="mt-1 line-clamp-2 text-xs text-slate-500">{visit.nextAction}</p>
                  </button>
                ))}
              </div>
            </div>
            <div className="rounded-xl border border-white/10 bg-white/[0.04] p-3">
              <div className="mb-3 flex items-center justify-between">
                <h3 className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-200">Repair work in progress</h3>
                <span className="rounded-full bg-violet-400 px-2 py-0.5 text-xs font-bold text-[#0f1c3f]">{data.happeningNow.repairs.length}</span>
              </div>
              <div className="space-y-3">
                {data.happeningNow.repairs.length === 0 ? <p className="py-4 text-center text-sm text-slate-400">No active repair work.</p> : data.happeningNow.repairs.map((repair) => (
                  <ActiveRepairCard key={repair.repairOrderId} repair={repair} onOpen={() => openRo(repair.repairOrderId)} />
                ))}
              </div>
            </div>
          </div>
        )}
      </section>

      <div data-testid="service-command-grid" className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <section id="service-upcoming-visits" data-testid="service-upcoming-visits" className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h2 className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">Upcoming service visits</h2>
              <p className="mt-1 text-sm text-slate-500">Today and the next 7 days</p>
            </div>
            <button type="button" className="text-xs font-medium text-slate-600 underline-offset-2 hover:underline" onClick={() => setLocation("/calendar/field")}>Field calendar</button>
          </div>
          <VisitGroup title="Today" visits={data.upcoming.today} prominent onOpen={openVisit} />
          <VisitGroup title="Tomorrow" visits={data.upcoming.tomorrow} onOpen={openVisit} />
          <VisitGroup title="Later this week" visits={data.upcoming.later} onOpen={openVisit} />
          {data.upcoming.today.length + data.upcoming.tomorrow.length + data.upcoming.later.length === 0 ? (
            <p className="mt-4 text-sm text-slate-500">No visits scheduled in the next 7 days.</p>
          ) : null}
        </section>

        <section id="service-latest-reports" data-testid="service-latest-reports" className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <h2 className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">Latest SVR reports</h2>
          {reports.length === 0 ? (
            <p className="mt-4 text-sm text-slate-500">No recent field reports.</p>
          ) : (
            <ul className="mt-2 divide-y divide-slate-100">
              {reports.map((report) => (
                <li key={report.id}>
                  <RowButton testId="service-report-row" onClick={() => openVisit(report.id)}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="font-mono text-[11px] text-slate-500">{report.visitNumber}</p>
                        <p className="truncate text-sm font-semibold text-slate-900">{report.customerName}</p>
                        <p className="text-xs text-slate-500">{report.technicianName || "Technician"} · {formatSingaporeDateTime(report.occurredAt, { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit", hour12: false })}</p>
                        {report.excerpt ? <p className="mt-1 line-clamp-2 text-sm text-slate-700">{report.excerpt}</p> : null}
                      </div>
                      <span className="shrink-0 rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-600">{report.picReview}</span>
                    </div>
                  </RowButton>
                </li>
              ))}
            </ul>
          )}
          {hiddenReports > 0 && !reportsOpen ? (
            <button type="button" data-testid="service-reports-view-all" className="mt-3 text-sm font-medium text-slate-700 underline-offset-2 hover:underline" onClick={() => setReportsOpen(true)}>
              View all
            </button>
          ) : null}
          {reportsOpen && hiddenReports > 0 ? (
            <button type="button" className="mt-3 text-sm font-medium text-slate-700 underline-offset-2 hover:underline" onClick={() => setReportsOpen(false)}>
              Show less
            </button>
          ) : null}
        </section>

        <section id="service-outstanding-ros" data-testid="service-outstanding-ros" className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h2 className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">Outstanding repair orders</h2>
              <p className="mt-1 text-sm text-slate-500">Open items needing Service attention</p>
            </div>
            <button type="button" className="text-xs font-medium text-slate-600 underline-offset-2 hover:underline" onClick={() => setLocation("/repair-orders")}>View all</button>
          </div>
          {data.repairProgress.needsAttention.length === 0 ? (
            <p className="mt-4 text-sm text-slate-500">Nothing is waiting on Service right now.</p>
          ) : (
            <ul className="mt-2 divide-y divide-slate-100">
              {data.repairProgress.needsAttention.map((job) => (
                <li key={job.id}>
                  <RowButton testId="service-attention-row" onClick={() => openRo(job.id)}>
                    <p className="font-mono text-[11px] text-slate-500">{job.jobNumber}</p>
                    <p className="text-sm font-semibold text-slate-900">{job.customerName}</p>
                    <p className="text-xs text-slate-500">{job.equipment} · {job.assignedTechnicianName || "Unassigned"}</p>
                    <p className="mt-1 text-xs font-medium text-amber-800">{job.reason}</p>
                  </RowButton>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      <section id="service-ro-progress" data-testid="service-ro-progress" className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <h2 className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">Recent repair activity</h2>
        <div className="mt-4">
          <div>
            <h3 className="text-sm font-semibold text-slate-900">Recently progressed</h3>
            {data.repairProgress.recentlyProgressed.length === 0 ? (
              <p className="mt-3 text-sm text-slate-500">No recent repair progress recorded.</p>
            ) : (
              <ul className="mt-2 divide-y divide-slate-100">
                {data.repairProgress.recentlyProgressed.map((event) => (
                  <li key={event.id}>
                    <RowButton testId="service-progress-row" onClick={() => openRo(event.jobId)}>
                      <div className="flex items-start gap-3">
                        <span className={cn("mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full", ACTIVITY_DOT_CLASS[event.category])} aria-hidden />
                        <div className="min-w-0">
                          <p className="font-mono text-[11px] text-slate-500">{event.jobNumber}</p>
                          <p className="text-sm font-semibold text-slate-900">{event.customerName}</p>
                          <p className="text-xs text-slate-500">{event.equipment} · {event.assignedTechnicianName || "Unassigned"}</p>
                          <p className="mt-1 flex flex-wrap items-center gap-2 text-xs text-slate-700">
                            <span className={cn("rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide", ACTIVITY_BADGE_CLASS[event.category])}>{SERVICE_ACTIVITY_CATEGORY_LABEL[event.category]}</span>
                            {event.summary}
                            <span className="text-slate-400">{formatSingaporeDateTime(event.occurredAt, { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit", hour12: false })}</span>
                          </p>
                        </div>
                      </div>
                    </RowButton>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </section>

      <section id="service-recently-fixed" data-testid="service-recently-fixed" className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <h2 className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">Recently fixed</h2>
        {data.recentlyFixed.length === 0 ? (
          <p className="mt-4 text-sm text-slate-500">No repairs have been completed recently.</p>
        ) : (
          <ul className="mt-3 divide-y divide-slate-100">
            {data.recentlyFixed.map((job) => (
              <li key={job.id}>
                <RowButton testId="service-fixed-row" onClick={() => openRo(job.id)}>
                  <p className="font-mono text-[11px] text-slate-500">{job.jobNumber}</p>
                  <p className="text-sm font-semibold text-slate-900">{job.equipment}</p>
                  <p className="text-xs text-slate-600">Repair completed {job.whenLabel}{job.technicianName ? ` · ${job.technicianName}` : ""}</p>
                  <p className="text-xs text-slate-500">{job.customerName} · {job.statusLabel}</p>
                </RowButton>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function VisitGroup({
  title,
  visits,
  prominent,
  onOpen,
}: {
  title: string;
  visits: ServiceOperationsView["upcoming"]["today"];
  prominent?: boolean;
  onOpen: (id: number) => void;
}) {
  if (visits.length === 0) return null;
  return (
    <div className="mt-4">
      <h3 className={cn("text-sm font-semibold", prominent ? "text-slate-900" : "text-slate-700")}>{title}</h3>
      <ul className="mt-1 divide-y divide-slate-100">
        {visits.map((visit) => (
          <li key={visit.id}>
            <RowButton testId="service-upcoming-row" onClick={() => onOpen(visit.id)}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-mono text-[11px] text-slate-500">{visit.visitNumber}</p>
                  <p className="truncate text-sm font-semibold text-slate-900">{visit.customerName}</p>
                  <p className="text-xs text-slate-500">{visit.arrivalWindow || "Window not set"} · {visit.technicianName || "Unassigned"}{visit.picName ? ` · PIC ${visit.picName}` : ""}</p>
                  {visit.siteAddress ? <p className="truncate text-xs text-slate-500">{visit.siteAddress}</p> : null}
                </div>
                <div className="flex shrink-0 flex-col items-end gap-1">
                  {visit.visitType === "urgent" ? <span className="rounded-full bg-orange-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-orange-800">Urgent</span> : null}
                  {visit.overdue ? <span data-testid="service-visit-overdue" className="rounded-full bg-red-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-red-700">Overdue</span> : null}
                  {visit.notification === "failed" ? <span className="text-[10px] font-medium text-red-700">Notice failed</span> : null}
                  {visit.notification === "no_phone" ? <span className="text-[10px] font-medium text-slate-500">No phone</span> : null}
                </div>
              </div>
            </RowButton>
          </li>
        ))}
      </ul>
    </div>
  );
}
